import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  WS_EVENTS,
  type AgentStatusData,
  type HandoverData,
  type NotificationData,
  type SessionData,
  type VisitorData,
  type WebSocketCallbacks,
  type WebSocketMessage,
} from './websocket-shared'
import {
  WebSocketReadyState,
  WebSocketService,
  type WebSocketServiceState,
} from './services/websocket-service'
import { getWebSocketUrl } from './utils'

type UseWebSocketResult = {
  isConnected: boolean
  readyState: WebSocketReadyState
  connectionId: string | null
  lastJsonMessage: WebSocketMessage | null
  lastMessage: MessageEvent<any> | null
  sendMessage: (message: WebSocketMessage) => void
  sendRawMessage: (
    message: string | ArrayBuffer | SharedArrayBuffer | Blob | ArrayBufferView,
    keep?: boolean
  ) => void
  disconnect: (code?: number, reason?: string) => void
  reconnect: () => void
  registerAgent: (agentId: number, sessionId?: string) => void
  registerUser: (sessionId: string) => void
  updateAgentStatus: (isOnline: boolean) => void
  requestHandover: (
    sessionId: string,
    reason?: string,
    priority?: string
  ) => void
}

const DEFAULT_STATE: WebSocketServiceState = {
  readyState: WebSocketReadyState.CLOSED,
  isConnected: false,
  connectionId: null,
  lastMessage: null,
  lastJsonMessage: null,
}

export function useWebSocket(
  url: string,
  callbacks: WebSocketCallbacks = {}
): UseWebSocketResult {
  const effectiveUrl = url || getWebSocketUrl()
  const service = useMemo(() => WebSocketService.getInstance(), [])
  const callbacksRef = useRef<WebSocketCallbacks>(callbacks)
  const [state, setState] = useState<WebSocketServiceState>(() =>
    effectiveUrl ? service.getState(effectiveUrl) : DEFAULT_STATE
  )

  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  useEffect(() => {
    if (!effectiveUrl) {
      return
    }

    const unsubscribe = service.register(
      effectiveUrl,
      callbacksRef,
      nextState => {
        setState(nextState)
      }
    )

    return () => {
      unsubscribe()
      service.release(effectiveUrl)
    }
  }, [effectiveUrl, service])

  const sendMessage = useCallback(
    (message: WebSocketMessage) => {
      if (!effectiveUrl) {
        return
      }
      service.sendJsonMessage(effectiveUrl, message)
    },
    [effectiveUrl, service]
  )

  const sendRawMessage = useCallback(
    (
      message:
        | string
        | ArrayBuffer
        | SharedArrayBuffer
        | Blob
        | ArrayBufferView,
      keep = false
    ) => {
      if (!effectiveUrl) {
        return
      }
      service.sendRawMessage(effectiveUrl, message, keep)
    },
    [effectiveUrl, service]
  )

  const disconnect = useCallback(
    (code?: number, reason?: string) => {
      if (!effectiveUrl) {
        return
      }
      service.disconnect(effectiveUrl, code, reason)
    },
    [effectiveUrl, service]
  )

  const reconnect = useCallback(() => {
    if (!effectiveUrl) {
      return
    }
    service.reconnect(effectiveUrl)
  }, [effectiveUrl, service])

  const registerAgent = useCallback(
    (agentId: number, sessionId?: string) => {
      if (!effectiveUrl) {
        return
      }
      service.registerAgent(effectiveUrl, agentId, sessionId)
    },
    [effectiveUrl, service]
  )

  const registerUser = useCallback(
    (sessionId: string) => {
      if (!effectiveUrl) {
        return
      }
      service.registerUser(effectiveUrl, sessionId)
    },
    [effectiveUrl, service]
  )

  const updateAgentStatus = useCallback(
    (isOnline: boolean) => {
      if (!effectiveUrl) {
        return
      }
      service.updateAgentStatus(effectiveUrl, isOnline)
    },
    [effectiveUrl, service]
  )

  const requestHandover = useCallback(
    (sessionId: string, reason?: string, priority = 'normal') => {
      if (!effectiveUrl) {
        return
      }
      service.requestHandover(effectiveUrl, sessionId, reason, priority)
    },
    [effectiveUrl, service]
  )

  const isConnected = state.readyState === WebSocketReadyState.OPEN

  return {
    isConnected,
    readyState: state.readyState,
    connectionId: state.connectionId,
    lastJsonMessage: state.lastJsonMessage,
    lastMessage: state.lastMessage,
    sendMessage,
    sendRawMessage,
    disconnect,
    reconnect,
    registerAgent,
    registerUser,
    updateAgentStatus,
    requestHandover,
  }
}

export { WS_EVENTS }
export type {
  AgentStatusData,
  SessionData,
  HandoverData,
  NotificationData,
  VisitorData,
  WebSocketCallbacks,
  WebSocketMessage,
}
export { WebSocketReadyState }
